import dayjs from "dayjs";
import { AudioCapture, pcm16ToBase64 } from "../components/Tasks/VoiceTaskCreate/audio-capture";
import { AudioPlayback } from "../components/Tasks/VoiceTaskCreate/audio-playback";
import { now, newTurn, afterPaint } from "../components/Tasks/VoiceTaskCreate/timing";
import nodeAPI from "../service/ApiServiceNode";
import logger from "../utils/logger";
import { buildVoiceWsUrl, DEFAULT_VOICE_MODEL, resolveAgentConfig } from "./voice-models";
import { SUPPORTED_LANGUAGES } from "./voice-prefs";
import { getVoiceStore } from "./voice-store-factory";
import {
    buildToolInstructions,
    executeRegisteredTool,
    getToolSchemasForAgent,
    hasTaskTools,
    hasTriageTools
} from "./tools/registry";

const SUPPORTED_LANGUAGE_CODES = new Set(SUPPORTED_LANGUAGES.map((l) => l.code));

const LANGUAGE_NAMES_EN = {
    en: "English",
    fr: "French",
    es: "Spanish",
    id: "Indonesian",
    zh: "Chinese",
    de: "German",
    it: "Italian",
    pt: "Portuguese",
    ja: "Japanese"
};
const OUTPUT_RATE = 24000;
const IDLE_DISCONNECT_MS = 30_000;

// OpenAI race-condition codes that are benign and should not disrupt the
// voice session or be reported to Sentry.
//   response_cancel_not_active — server VAD already cancelled the response
//     before our manual response.cancel arrived.
//   conversation_already_has_active_response — server VAD already created a
//     response before our manual response.create arrived.
const BENIGN_ERROR_CODES = new Set([
    "response_cancel_not_active",
    "conversation_already_has_active_response"
]);

// OpenAI provider-level codes that represent external billing/quota failures.
// These are NOT application bugs — they should not be reported to Sentry and
// the user should see a friendly message rather than the raw developer string.
const EXTERNAL_ERROR_CODES = new Set([
    "insufficient_quota",
    "rate_limit_exceeded"
]);

const DEFAULT_TASK_TOOL_KEYS = [
    "task.set_field",
    "task.lookup_person",
    "task.lookup_project",
    "task.clear_field",
    "task.read_back_draft",
    "task.commit",
    "task.cancel"
];

function defaultAgentConfig(model) {
    const resolved = resolveAgentConfig({ model: model || DEFAULT_VOICE_MODEL });
    return {
        code: "task",
        name: "Task",
        provider: resolved.provider,
        model: resolved.model,
        voice: resolved.voice,
        tool_keys: DEFAULT_TASK_TOOL_KEYS,
        handoff_target_codes: [],
        system_prompt: ""
    };
}

function normalizeAgentConfig(agentConfig, model) {
    const base = agentConfig || defaultAgentConfig(model);
    const resolved = resolveAgentConfig(base);
    return {
        ...base,
        provider: resolved.provider,
        model: resolved.model,
        voice: resolved.voice,
        tool_keys: Array.isArray(base.tool_keys) ? base.tool_keys : [],
        handoff_target_codes: Array.isArray(base.handoff_target_codes)
            ? base.handoff_target_codes
            : []
    };
}

function pronounFor(gender) {
    if (!gender) return "they";
    const normalized = String(gender).toLowerCase();
    if (["male", "m", "man"].includes(normalized)) return "he";
    if (["female", "f", "woman"].includes(normalized)) return "she";
    return "they";
}

function formatUserList(users) {
    if (!Array.isArray(users) || users.length === 0) {
        return "(no users loaded - ask the user to spell any name)";
    }
    return users
        .map((u) => {
            const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name || u.email;
            return name ? `- ${name} (${pronounFor(u.gender)})` : null;
        })
        .filter(Boolean)
        .slice(0, 400)
        .join("\n");
}

/**
 * Format the optional initial-context blob that the parent passes to the
 * VoiceClient constructor. Two shapes are supported:
 *
 *   - plain string OR object (legacy "handoff" payload from one agent to
 *     another) → rendered under "Previous agent handoff context:".
 *
 *   - { kind: "document", payload: { document, summary } } → rendered as a
 *     dedicated DOCUMENT + PRE_GENERATED_SUMMARY block for the
 *     Voice Document Reader. Returned as a labeled chunk so the formatter
 *     owns its own wrapper.
 *
 * Returns either an empty string (no context), a plain string for the
 * default handoff path, or an object { label, body } for kinds that want
 * a custom wrapper. buildInstructions() handles both shapes.
 */
function formatInitialContext(input) {
    if (!input) return "";

    if (typeof input === "object" && input.kind === "document") {
        const document = String(input.payload?.document || "").trim();
        const summary = String(input.payload?.summary || "").trim();
        if (!document) return "";
        const body =
            `<DOCUMENT>\n${document}\n</DOCUMENT>` +
            (summary
                ? `\n\n<PRE_GENERATED_SUMMARY>\n${summary}\n</PRE_GENERATED_SUMMARY>`
                : "");
        return {
            label: "User-provided document for this session:",
            body
        };
    }

    if (typeof input === "string") return input;
    try {
        return JSON.stringify(input, null, 2);
    } catch {
        return String(input);
    }
}

function summarizeTriageForPrompt(result) {
    if (!result) return "";
    const compact = {
        summary: result.summary || {},
        now: Array.isArray(result.now) ? result.now.slice(0, 5) : [],
        next: Array.isArray(result.next) ? result.next.slice(0, 6) : [],
        plan_blocks: Array.isArray(result.plan_blocks) ? result.plan_blocks.slice(0, 8) : []
    };
    try {
        return JSON.stringify(compact, null, 2).slice(0, 8000);
    } catch {
        return "";
    }
}

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export class VoiceClient {
    constructor({
        users = [],
        model = DEFAULT_VOICE_MODEL,
        language = "en",
        agentConfig = null,
        storeBundle = null,
        initialContext = null,
        onSaved = null,
        onCancelled = null,
        onHandoff = null
    } = {}) {
        this.agentConfig = normalizeAgentConfig(agentConfig, model);
        this.users = users;
        this.model = this.agentConfig.model;
        this.provider = this.agentConfig.provider;
        this.voice = this.agentConfig.voice;
        this.language = SUPPORTED_LANGUAGE_CODES.has(language) ? language : "en";
        this.storeBundle = storeBundle || getVoiceStore(this.agentConfig.code || "default");
        this.store = this.storeBundle.store;
        this.actions = this.storeBundle.actions;
        this.initialContext = initialContext;
        this.onSaved = onSaved;
        this.onCancelled = onCancelled;
        this.onHandoff = onHandoff;
        this.toolSchemas = getToolSchemasForAgent(this.agentConfig);

        this.ws = null;
        this.ctx = null;
        this.capture = null;
        this.playback = null;
        this.turnHasFirstAudio = false;
        this.turnHasFirstArgsDelta = false;
        this.responseInFlight = false;
        this.pendingCalls = new Map();
        this.toolOutputsAwaitingResponse = 0;
        // Late text deltas from a cancelled response (caused by user barge-in)
        // can arrive after onSpeechStarted finalizes the assistant bubble. If
        // we render them, they create an orphan continuation bubble after the
        // user's transcript. This flag suppresses those deltas until a new
        // response has clearly started (response.created or first audio delta).
        this.suppressLateAssistantText = false;
        this.busy = false;
        this.stopRequested = false;
        this.idleTimer = null;
        this.pendingHandoff = null;
        this.handoffInProgress = false;
        this.connectionGeneration = 0;
        this.activeUserMessageId = null;
    }

    armIdleTimer() {
        if (this.idleTimer) clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(() => {
            logger.info?.("[voice] idle for 30s; auto-disconnecting");
            this.actions.setConnection("idle", "Session ended after 30s of silence.");
            void this.stop();
        }, IDLE_DISCONNECT_MS);
    }

    clearIdleTimer() {
        if (this.idleTimer) {
            clearTimeout(this.idleTimer);
            this.idleTimer = null;
        }
    }

    async start() {
        if (this.busy || this.ws) return;
        this.busy = true;
        this.stopRequested = false;
        this.connectionGeneration += 1;
        this.actions.setConnection("connecting");

        try {
            if (hasTaskTools(this.agentConfig)) {
                await this.ensureUsersLoaded();
            }
            this.throwIfStopRequested();

            const tokenRes = await nodeAPI.post(
                `/voice/token?provider=${encodeURIComponent(this.provider)}` +
                    `&model=${encodeURIComponent(this.model)}` +
                    `&voice=${encodeURIComponent(this.voice)}`
            );
            this.throwIfStopRequested();
            const data = tokenRes?.data?.data || tokenRes?.data || {};
            const value = extractTokenValue(data);
            if (!value) {
                throw new Error(`unexpected token response shape: ${JSON.stringify(data).slice(0, 200)}`);
            }

            const ctxOptions = this.provider === "openai" ? { sampleRate: 24000 } : undefined;
            this.ctx = ctxOptions ? new AudioContext(ctxOptions) : new AudioContext();
            if (this.ctx.state === "suspended") await this.ctx.resume();
            this.throwIfStopRequested();

            this.playback = new AudioPlayback(this.ctx, OUTPUT_RATE);
            this.capture = new AudioCapture({
                ctx: this.ctx,
                onPcmChunk: (pcm) => this.sendAudioChunk(pcm)
            });
            this.throwIfStopRequested();

            const wsUrl = buildVoiceWsUrl(this.agentConfig);
            logger.info?.(`[voice] connecting to ${this.provider}/${this.model}/${this.voice}`);
            const subprotocols =
                this.provider === "openai"
                    ? ["realtime", `openai-insecure-api-key.${value}`]
                    : [`xai-client-secret.${value}`];

            const ws = new WebSocket(wsUrl, subprotocols);
            this.ws = ws;
            ws.binaryType = "arraybuffer";
            ws.onopen = () => this.handleOpen();
            ws.onmessage = (e) => this.handleMessage(e);
            ws.onerror = () => {
                void this.handleTransportFailure(ws, "Voice connection failed. Please try again.");
            };
            ws.onclose = (e) => {
                void this.handleTransportClose(ws, e);
            };
        } catch (err) {
            if (err?.code === "VOICE_START_CANCELLED" || this.stopRequested) {
                await this.stopInternal();
            } else {
                const message = getStartupErrorMessage(err);
                await this.stopInternal({ preserveConnectionState: true });
                this.actions.setConnection("error", message);
            }
        } finally {
            this.busy = false;
            if (this.stopRequested) {
                await this.stop();
            }
        }
    }

    throwIfStopRequested() {
        if (!this.stopRequested) return;
        const err = new Error("Voice startup cancelled");
        err.code = "VOICE_START_CANCELLED";
        throw err;
    }

    async ensureUsersLoaded() {
        const fallback = Array.isArray(this.users) ? this.users : [];
        try {
            const res = await nodeAPI.get("/user/search", {
                params: { q: "", limit: 500 }
            });
            const rawUsers = res?.data?.data?.users || res?.data?.users || [];
            const users = Array.isArray(rawUsers) ? rawUsers : [];
            this.users = users.map((u) => {
                if (u.first_name || u.last_name) {
                    return {
                        id: u.id,
                        first_name: u.first_name,
                        last_name: u.last_name,
                        email: u.email,
                        gender: u.gender || null
                    };
                }
                const [first, ...rest] = (u.name || u.email || "").split(" ");
                return {
                    id: u.id,
                    first_name: first || "",
                    last_name: rest.join(" "),
                    email: u.email,
                    gender: u.gender || null
                };
            });
            this.actions.setActiveUsers(this.users);
            logger.info?.(`[voice] loaded ${this.users.length} active users for context`);
        } catch (err) {
            logger.error("[voice] failed to load active users", err);
            this.users = fallback;
            this.actions.setActiveUsers(this.users);
        }
    }

    async stop() {
        this.stopRequested = true;
        if (this.busy) return;
        this.busy = true;
        try {
            await this.stopInternal();
        } finally {
            this.busy = false;
        }
    }

    async gracefulDisconnect() {
        await this.waitForPlaybackDrain();
        await this.stopInternal();
    }

    async pauseForWork(message = "Working...") {
        this.actions.setConnection("working", message);
        await this.stopInternal({ preserveConnectionState: true });
        this.actions.setConnection("working", message);
    }

    isRealtimeOpen() {
        return Boolean(this.ws && this.ws.readyState === WebSocket.OPEN);
    }

    async waitForPlaybackDrain() {
        const remainingSeconds = Math.max(
            0,
            (this.playback?.playhead || 0) - (this.ctx?.currentTime || 0)
        );
        if (remainingSeconds > 0) {
            await sleep(Math.min(3500, remainingSeconds * 1000 + 150));
        }
    }

    async stopInternal({ preserveConnectionState = false } = {}) {
        this.clearIdleTimer();
        const ws = this.ws;
        this.ws = null;
        try {
            ws?.close(1000);
        } catch {
            // ignore
        }
        await this.capture?.stop?.();
        this.capture = null;
        this.playback?.flush?.();
        this.playback = null;
        if (this.ctx && this.ctx.state !== "closed") {
            try {
                await this.ctx.close();
            } catch {
                // ignore
            }
        }
        this.ctx = null;
        this.pendingCalls.clear();
        this.toolOutputsAwaitingResponse = 0;
        this.responseInFlight = false;
        this.stopRequested = false;
        if (!preserveConnectionState) {
            this.actions.setConnection("idle");
        }
        this.actions.setPendingMutation(false);
    }

    async handleTransportFailure(ws, message) {
        if (this.ws !== ws) return;
        await this.stopInternal({ preserveConnectionState: true });
        this.actions.setConnection("error", message);
    }

    async handleTransportClose(ws, event = {}) {
        if (this.ws !== ws) return;
        const code = event?.code;
        await this.stopInternal({ preserveConnectionState: true });
        if (code && code !== 1000) {
            this.actions.setConnection("error", `Voice connection closed (${code}). Please try again.`);
        } else {
            this.actions.setConnection("idle");
        }
    }

    handleOpen() {
        if (!this.ctx || !this.capture) return;
        if (this.stopRequested) {
            void this.stopInternal();
            return;
        }
        this.send({
            type: "session.update",
            session: this.buildSessionUpdate(this.ctx.sampleRate)
        });
        this.capture.start().then(
            () => {
                if (this.stopRequested) {
                    void this.stopInternal();
                    return;
                }
                this.actions.setConnection("open");
                this.armIdleTimer();
            },
            (err) => {
                if (this.stopRequested) {
                    void this.stopInternal();
                    return;
                }
                this.actions.setConnection("error", err?.message || "mic failed");
            }
        );
    }

    buildInstructions() {
        const systemPrompt = String(this.agentConfig.system_prompt || "").trim();
        const fallbackPrompt = `You are the ${this.agentConfig.name || this.agentConfig.code || "voice"} voice agent.`;
        const prompt = systemPrompt || fallbackPrompt;
        const today = dayjs().format("dddd, D MMMM YYYY");
        const langName = LANGUAGE_NAMES_EN[this.language] || "English";
        const chunks = [
            prompt,
            `Today is ${today}.`,
            // Whisper transcription language pins the input side; this pins the
            // output side. Without it, OpenAI Realtime drifts to other languages
            // mid-conversation when a phrase trips its phoneme detector.
            `Always respond in ${langName}, regardless of any phrase that may sound like another language.`,
            buildToolInstructions(this.agentConfig)
        ];

        if (hasTaskTools(this.agentConfig)) {
            chunks.push(
                `\nThe active users available for task assignment are:\n${formatUserList(this.users)}`
            );
        }

        if (hasTriageTools(this.agentConfig)) {
            const triageSummary = summarizeTriageForPrompt(this.store.getState().triage?.result);
            if (triageSummary) {
                chunks.push(`\nLatest task triage result:\n${triageSummary}`);
            }
        }

        const contextBlock = formatInitialContext(this.initialContext);
        if (contextBlock) {
            if (typeof contextBlock === "object" && contextBlock.body) {
                chunks.push(`\n${contextBlock.label}\n${contextBlock.body}`);
            } else {
                chunks.push(`\nPrevious agent handoff context:\n${contextBlock}`);
            }
        }

        if (this.agentConfig.handoff_target_codes?.length) {
            chunks.push(
                `\nOnly hand off to these target agent codes: ${this.agentConfig.handoff_target_codes.join(", ")}.`
            );
        }

        return chunks.filter(Boolean).join("\n\n");
    }

    buildSessionUpdate(inputRate) {
        const instructions = this.buildInstructions();
        const audioOutput = {
            voice: this.voice,
            format: { type: "audio/pcm", rate: OUTPUT_RATE }
        };
        const transcription = { model: "whisper-1", language: this.language || "en" };

        if (this.provider === "openai") {
            const session = {
                type: "realtime",
                model: this.model,
                instructions,
                tools: this.toolSchemas,
                audio: {
                    input: {
                        format: { type: "audio/pcm", rate: inputRate },
                        turn_detection: { type: "server_vad" },
                        transcription
                    },
                    output: audioOutput
                }
            };
            if (this.model === "gpt-realtime-2") {
                session.reasoning = { effort: "low" };
            }
            return session;
        }

        return {
            voice: this.voice,
            instructions,
            turn_detection: { type: "server_vad", create_response: false },
            tools: this.toolSchemas,
            input_audio_transcription: transcription,
            audio: {
                input: { format: { type: "audio/pcm", rate: inputRate } },
                output: audioOutput
            }
        };
    }

    sendAudioChunk(pcm16) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        this.send({
            type: "input_audio_buffer.append",
            audio: pcm16ToBase64(pcm16)
        });
    }

    send(payload) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        this.ws.send(JSON.stringify(payload));
    }

    handleMessage(e) {
        let msg;
        try {
            msg = JSON.parse(typeof e.data === "string" ? e.data : "");
        } catch {
            return;
        }
        const type = msg?.type;
        if (!type) return;

        switch (type) {
            case "input_audio_buffer.speech_started":
                this.onSpeechStarted();
                break;
            case "input_audio_buffer.speech_stopped":
                this.onSpeechStopped();
                break;
            case "response.created":
                this.onResponseCreated();
                break;
            case "response.output_audio.delta":
            case "response.audio.delta":
                this.onAudioDelta(msg);
                break;
            case "response.output_audio_transcript.delta":
            case "response.audio_transcript.delta":
                this.onAssistantTextDelta(msg);
                break;
            case "response.output_audio_transcript.done":
            case "response.audio_transcript.done":
                break;
            case "response.function_call_arguments.delta":
                this.onArgsDelta(msg);
                break;
            case "response.function_call_arguments.done":
                void this.onArgsDone(msg);
                break;
            case "response.done":
                void this.onResponseDone();
                break;
            case "conversation.item.input_audio_transcription.delta":
                this.onUserTranscriptDelta(msg);
                break;
            case "conversation.item.input_audio_transcription.completed":
                this.onUserTranscriptCompleted(msg);
                break;
            case "error":
                this.onServerError(msg);
                break;
            default:
                break;
        }
    }

    onSpeechStarted() {
        this.armIdleTimer();
        // OpenAI's server VAD automatically cancels the active response when it
        // detects new speech. Sending a manual response.cancel races with that
        // server-side cancel and produces response_cancel_not_active. xAI does
        // not auto-cancel, so we keep the manual cancel for that provider only.
        if (this.responseInFlight && this.provider !== "openai") {
            this.send({ type: "response.cancel" });
        }
        this.playback?.hardStop?.();
        this.responseInFlight = false;
        this.toolOutputsAwaitingResponse = 0;
        // Drop any late text deltas from the cancelled response. Cleared when
        // the next response begins (response.created or its first audio delta).
        this.suppressLateAssistantText = true;
        this.actions.finalizeAssistantMessage();

        const turn = newTurn();
        turn.markers.t_speech_start = now();
        turn.userMessageId = `u-${turn.id}`;
        this.currentTurn = turn;
        this.activeUserMessageId = turn.userMessageId;
        this.actions.startUserMessage(turn.userMessageId);
        this.turnHasFirstAudio = false;
        this.turnHasFirstArgsDelta = false;
    }

    onSpeechStopped() {
        if (this.currentTurn) this.currentTurn.markers.t_speech_stop = now();
        // OpenAI's server VAD auto-creates a response when speech stops. Sending
        // our own response.create races and produces "Conversation already has
        // an active response in progress: resp_...". xAI runs with
        // create_response: false, so it relies on this manual send to pin the
        // voice on every turn.
        if (this.provider === "openai") return;
        this.send(this.responseCreatePayload());
    }

    responseCreatePayload() {
        if (this.provider === "openai") {
            return { type: "response.create", response: {} };
        }
        return { type: "response.create", response: { voice: this.voice } };
    }

    onAudioDelta(msg) {
        const b64 = msg.delta || msg.audio;
        if (!b64) return;
        this.responseInFlight = true;
        // Audio for a fresh response means the post-barge-in cancel is over.
        // Belt-and-suspenders alongside the response.created handler below.
        this.suppressLateAssistantText = false;
        if (!this.turnHasFirstAudio) {
            this.turnHasFirstAudio = true;
            if (this.currentTurn) this.currentTurn.markers.t_first_audio = now();
        }
        this.playback?.enqueueBase64Pcm16(b64);
    }

    onResponseCreated() {
        // Earliest signal that a new response has begun — clear the late-text
        // suppression flag set by onSpeechStarted so this response's deltas
        // render normally.
        this.suppressLateAssistantText = false;
    }

    onAssistantTextDelta(msg) {
        // Discard text fragments from a response that was cancelled by user
        // barge-in. Without this guard the delta would either glue onto the
        // already-finalized assistant bubble (creating a "\n"-separated tail)
        // or — if it arrives after the user's transcript — render as an
        // orphan continuation bubble in the wrong order.
        if (this.suppressLateAssistantText) return;
        const delta = msg.delta || "";
        if (delta) this.actions.appendAssistantText(delta);
    }

    onArgsDelta(msg) {
        this.responseInFlight = true;
        if (!this.turnHasFirstArgsDelta) {
            this.turnHasFirstArgsDelta = true;
            this.actions.setPendingMutation(true);
        }
        const id = msg.call_id;
        if (!id) return;
        const name = msg.name || "";
        const delta = msg.delta || "";
        const existing = this.pendingCalls.get(id) || { call_id: id, name, args: "" };
        if (name && !existing.name) existing.name = name;
        existing.args += delta;
        this.pendingCalls.set(id, existing);
    }

    async onArgsDone(msg) {
        const id = msg.call_id || "";
        const name = msg.name || this.pendingCalls.get(id)?.name || "";
        const argsRaw = msg.arguments || this.pendingCalls.get(id)?.args || "{}";
        this.pendingCalls.delete(id);

        let parsed = {};
        try {
            parsed = JSON.parse(argsRaw);
        } catch {
            // tolerate partial provider payloads
        }

        if (this.currentTurn) {
            this.currentTurn.toolName = name;
            this.currentTurn.markers.t_args_done = now();
        }

        const executionGeneration = this.connectionGeneration;
        const result = await executeRegisteredTool(name, parsed, this.buildToolContext());

        afterPaint().then((t) => {
            if (this.currentTurn) this.currentTurn.markers.t_ui_commit = t;
            this.actions.setPendingMutation(false);
        });

        this.actions.discardPartialAssistantMessage();
        if (executionGeneration === this.connectionGeneration && this.isRealtimeOpen()) {
            this.send({
                type: "conversation.item.create",
                item: {
                    type: "function_call_output",
                    call_id: id,
                    output: JSON.stringify(result)
                }
            });
            this.queueToolOutputFollowUp();
        }
    }

    buildToolContext() {
        return {
            client: this,
            store: this.store,
            actions: this.actions,
            agentConfig: this.agentConfig,
            onSaved: this.onSaved,
            onCancelled: this.onCancelled,
            requestHandoff: (handoff) => {
                this.pendingHandoff = handoff;
            }
        };
    }

    queueToolOutputFollowUp() {
        this.toolOutputsAwaitingResponse++;
        this.maybeCreateToolFollowUpResponse();
    }

    maybeCreateToolFollowUpResponse() {
        if (this.toolOutputsAwaitingResponse <= 0 || this.responseInFlight) {
            return;
        }
        this.toolOutputsAwaitingResponse = 0;
        this.responseInFlight = true;
        this.send(this.responseCreatePayload());
    }

    async onResponseDone() {
        this.responseInFlight = false;
        this.actions.setPendingMutation(false);
        this.actions.finalizeAssistantMessage();
        this.maybeCreateToolFollowUpResponse();
        await this.maybePerformPendingHandoff();
    }

    async maybePerformPendingHandoff() {
        if (
            !this.pendingHandoff ||
            this.handoffInProgress ||
            this.responseInFlight ||
            this.toolOutputsAwaitingResponse > 0
        ) {
            return;
        }

        this.handoffInProgress = true;
        const handoff = this.pendingHandoff;
        this.pendingHandoff = null;
        await this.gracefulDisconnect();
        this.onHandoff?.(handoff);
    }

    onUserTranscriptDelta(msg) {
        const text = msg.delta || "";
        if (!text) return;
        const itemId = msg.item_id || msg.itemId || null;
        if (this.activeUserMessageId) {
            this.actions.updateUserMessage(this.activeUserMessageId, text, {
                append: true,
                partial: true,
                itemId
            });
        } else {
            this.actions.setInterimTranscript(text);
        }
    }

    onUserTranscriptCompleted(msg) {
        const text = msg.transcript || "";
        const itemId = msg.item_id || msg.itemId || null;
        if (this.activeUserMessageId) {
            this.actions.updateUserMessage(this.activeUserMessageId, text, {
                partial: false,
                itemId,
                removeIfEmpty: true
            });
            this.activeUserMessageId = null;
        } else if (text) {
            this.actions.appendUserMessage(text, itemId);
        }
    }

    onServerError(msg) {
        const err = msg?.error || msg || {};
        const code = typeof err.code === "string" ? err.code : "";
        const message =
            (typeof err.message === "string" && err.message) ||
            code ||
            "server error";

        if (BENIGN_ERROR_CODES.has(code)) {
            // Race-condition artefact — the server already handled it on its
            // side. Log at warn level so devs can see it locally, but do not
            // report to Sentry and do not disrupt the voice session.
            logger.warn?.("[voice realtime warn]", msg);
            return;
        }

        if (EXTERNAL_ERROR_CODES.has(code)) {
            // External provider billing/quota errors — not application bugs.
            // Log at warn level so the issue is visible in local dev, but do
            // not forward to Sentry (false-positive noise) and show a friendly
            // message to the user instead of the raw OpenAI developer string.
            logger.warn?.("[voice realtime external]", msg);
            this.actions.setConnection(
                "error",
                "Voice service is temporarily unavailable. Please try again later."
            );
            return;
        }

        // Non-benign, non-external server errors: log at error level (which
        // routes to Sentry.captureException via logger.js) and set the UI to
        // error state. Do NOT also call Sentry.captureMessage — logger.error
        // already captures the exception, and a second direct call would
        // create duplicate Sentry events.
        logger.error("[voice realtime error]", msg);
        this.actions.setConnection("error", message);
    }
}

export function extractTokenValue(data) {
    if (!data || typeof data !== "object") return undefined;
    if (typeof data.value === "string") return data.value;
    if (typeof data.token === "string") return data.token;
    const clientSecret = data.client_secret;
    if (typeof clientSecret === "string") return clientSecret;
    if (clientSecret && typeof clientSecret === "object" && typeof clientSecret.value === "string") {
        return clientSecret.value;
    }
    return undefined;
}

function getStartupErrorMessage(err) {
    const responseData = err?.response?.data;
    const backendMessage =
        responseData?.data?.message ||
        responseData?.data?.error ||
        responseData?.message ||
        responseData?.error;
    if (typeof backendMessage === "string" && backendMessage.trim()) {
        return backendMessage.trim();
    }
    return String(err?.message || err);
}
